// /calendar/events/:id/edit — edit page (W40.1 F7).
//
// Shares one form with `/calendar/events/new` (`EventForm`); the only
// difference is that an existing row is loaded first and submitted as a PATCH
// instead of a POST.
//
// Which is the honest scope for this route. An event is one row — one
// continuous interval — so editing it edits that row. A selection that was
// scattered at creation time is already N rows with N ids, and editing "the
// 整月休假" means editing the specific row the user tapped. ADR-0101 records
// that cost rather than hiding it; the create page says so on screen before the
// user commits.

import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { EventForm } from "@/components/calendar/EventForm";
import { toEditorItem } from "@/components/calendar/eventViewModel";
import { successMessage } from "@/components/calendar/eventFanOut";
import { getEvent } from "@/lib/api/calendar";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useToast } from "@/components/ui/toast";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { PageContainer } from "@/components/layout";

export function CalendarEventEditPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { push: pushToast } = useToast();

  const eventQuery = useQuery({
    queryKey: ["calendar-event", id],
    queryFn: () => getEvent(id),
    enabled: Boolean(id),
  });

  /**
   * 目的地是**事件詳情頁**，而那一頁**沒有撤銷條**（它的撤銷條屬於「刪除」）。
   * 所以「已更新」由這裡說。
   *
   * **新增**那一側不一樣：它的目的地是月曆頁，那裡的撤銷條已經說了同一句話
   * 並多給一個「復原」動作 —— 再說一次就是同一句話說兩遍（ADR-0215）。
   */
  const onDone = useCallback(
    ({ dayCount, isEdit }: { ids: string[]; dayCount: number; isEdit: boolean }) => {
      pushToast({ variant: "success", message: successMessage(dayCount, isEdit) });
      nav(`/calendar/events/${id}`);
    },
    [nav, id, pushToast],
  );

  // `toEditorItem` is the same adapter the detail page uses, so 「TA 的事件」
  // cannot read as 「我的事件」 on one screen and not the other.
  const editorItem = useMemo(
    () => (eventQuery.data ? toEditorItem(eventQuery.data, user?.id) : null),
    [eventQuery.data, user?.id],
  );

  if (eventQuery.isLoading) {
    return (
      <div data-testid="calendar-event-edit-loading" className="mx-auto w-full px-s-4 py-s-5 md:max-w-[390px]">
        <LoadingState shape="card" count={3} />
      </div>
    );
  }

  if (eventQuery.isError || !editorItem) {
    return (
      <PageContainer width="default" vertical="loose">
        <ErrorState
          title="無法載入事件"
          description="請稍候再試一次"
          onRetry={() => void eventQuery.refetch()}
        />
      </PageContainer>
    );
  }

  return <EventForm editing={editorItem} backTo={`/calendar/events/${id}`} onDone={onDone} />;
}
